import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../hooks/useAuth';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import DataTable from '../../components/Common/DataTable';
import { ArrowLeftIcon, PencilIcon, CalendarIcon, KeyIcon } from '@heroicons/react/24/outline';

const DriverDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { get, loading } = useApi();
  const { user: currentUser } = useAuth();
  const [driver, setDriver] = useState(null);
  const [trips, setTrips] = useState([]);
  const [tripsPageInfo, setTripsPageInfo] = useState({
    totalElements: 0,
    totalPages: 0,
    currentPage: 0,
    size: 10
  });
  const [activeTab, setActiveTab] = useState('info');
  const [dateRange, setDateRange] = useState({
    fromDate: '',
    toDate: ''
  });
  const [loadingTrips, setLoadingTrips] = useState(false);

  const isAdmin = currentUser?.roles?.includes('ADMIN') || false;

  useEffect(() => {
    fetchDriver();
  }, [id]);

  useEffect(() => {
    if (location.state?.tab === 'trips') {
      setActiveTab('trips');
    }
  }, [location.state]);

  useEffect(() => {
    if (activeTab === 'trips') {
      fetchDriverTrips();
    }
  }, [activeTab, tripsPageInfo.currentPage, dateRange]);

  const fetchDriver = async () => {
    const { data } = await get(`/api/drivers/${id}`);
    if (data) setDriver(data);
  };

  const fetchDriverTrips = async () => {
    setLoadingTrips(true);
    
    let endpoint;
    if (dateRange.fromDate || dateRange.toDate) {
      endpoint = `/api/drivers/${id}/trips/details`;
      const params = new URLSearchParams();
      if (dateRange.fromDate) params.append('fromDate', dateRange.fromDate);
      if (dateRange.toDate) params.append('toDate', dateRange.toDate);
      if (params.toString()) endpoint += `?${params.toString()}`;
    } else {
      endpoint = `/api/drivers/${id}/trips?pageNumber=${tripsPageInfo.currentPage}&itemsPerPage=${tripsPageInfo.size}`;
    }
    
    const { data } = await get(endpoint);
    
    if (data) {
      if (dateRange.fromDate || dateRange.toDate) {
        setTrips(data.recentTrips || []);
        setTripsPageInfo(prev => ({
          ...prev,
          totalElements: data.recentTrips?.length || 0,
          totalPages: 1
        }));
      } else {
        setTrips(data.content || []);
        setTripsPageInfo({
          totalElements: data.totalElements || 0,
          totalPages: data.totalPages || 0,
          currentPage: data.number || 0,
          size: data.size || 10
        });
      }
    }
    
    setLoadingTrips(false);
  };

  const handleDateRangeChange = (e) => {
    const { name, value } = e.target;
    setDateRange(prev => ({ ...prev, [name]: value }));
  };

  const clearDateRange = () => {
    setDateRange({ fromDate: '', toDate: '' });
  };

  const tripColumns = [
    { header: 'ID', accessor: 'id' },
    { header: 'Label', accessor: (row) => row.label || `Trip #${row.id}` },
    { header: 'Type', accessor: (row) => row.type || 'Standard' },
    { 
      header: 'Vehicle', 
      accessor: (row) => row.vehicle ? `${row.vehicle.brand} - ${row.vehicle.plates}` : 'Unknown'
    },
    { 
      header: 'Started', 
      accessor: (row) => row.startDate || row.startedAt ? new Date(row.startDate || row.startedAt).toLocaleString() : '-'
    },
    { 
      header: 'Status', 
      accessor: (row) => (
        <span className={`px-2 py-1 rounded-full text-xs ${
          (row.endDate || row.endedAt) ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 animate-pulse'
        }`}>
          {(row.endDate || row.endedAt) ? 'Completed' : 'In Progress'}
        </span>
      )
    },
    {
      header: 'Actions',
      accessor: (row) => (
        <button
          onClick={() => navigate(`/trips/${row.id}`)}
          className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300 text-sm font-medium"
        >
          View Trip
        </button>
      )
    }
  ];

  if (loading) return <LoadingSpinner />;
  if (!driver) return <div className="p-6 text-gray-900 dark:text-white">Driver not found</div>;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <button
            onClick={() => navigate('/drivers/filter')}
            className="mr-4 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <ArrowLeftIcon className="h-6 w-6" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Driver Details</h1>
        </div>
        
        <div className="flex space-x-2">
          {isAdmin && (
            <button
              onClick={() => navigate(`/drivers/edit/${id}`)}
              className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center space-x-2"
            >
              <PencilIcon className="h-5 w-5" />
              <span>Edit Driver</span>
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('info')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'info'
                ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            Driver Information
          </button>
          <button
            onClick={() => setActiveTab('trips')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'trips'
                ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            Trip History
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'info' ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-sm text-gray-500 dark:text-gray-400">ID</label>
              <p className="text-lg font-medium text-gray-900 dark:text-white">{driver.id}</p>
            </div>
            <div>
              <label className="text-sm text-gray-500 dark:text-gray-400">User ID</label>
              <p className="text-lg font-medium text-gray-900 dark:text-white">{driver.userId || '-'}</p>
            </div>
            <div>
              <label className="text-sm text-gray-500 dark:text-gray-400">Full Name</label>
              <p className="text-lg font-medium text-gray-900 dark:text-white">{driver.fullName}</p>
            </div>
            <div>
              <label className="text-sm text-gray-500 dark:text-gray-400">Mobile Number</label>
              <p className="text-lg font-medium text-gray-900 dark:text-white">{driver.mobileNumber || '-'}</p>
            </div>
            <div>
              <label className="text-sm text-gray-500 dark:text-gray-400">Other ID</label>
              <p className="text-lg font-medium text-gray-900 dark:text-white">{driver.otherId || '-'}</p>
            </div>
            <div className="md:col-span-2">
              <label className="text-sm text-gray-500 dark:text-gray-400">Address</label>
              <p className="text-lg font-medium text-gray-900 dark:text-white">{driver.address || '-'}</p>
            </div>
            <div>
              <label className="text-sm text-gray-500 dark:text-gray-400">Country</label>
              <p className="text-lg font-medium text-gray-900 dark:text-white">{driver.country || '-'}</p>
            </div>
            <div>
              <label className="text-sm text-gray-500 dark:text-gray-400">State</label>
              <p className="text-lg font-medium text-gray-900 dark:text-white">{driver.drvState || '-'}</p>
            </div>
            <div>
              <label className="text-sm text-gray-500 dark:text-gray-400">Postal Code</label>
              <p className="text-lg font-medium text-gray-900 dark:text-white">{driver.postalCode || '-'}</p>
            </div>
            <div>
              <label className="text-sm text-gray-500 dark:text-gray-400">Birth Date</label>
              <p className="text-lg font-medium text-gray-900 dark:text-white">
                {driver.birthDate ? new Date(driver.birthDate).toLocaleDateString() : '-'}
              </p>
            </div>
            <div>
              <label className="text-sm text-gray-500 dark:text-gray-400">Started At</label>
              <p className="text-lg font-medium text-gray-900 dark:text-white">
                {driver.startedAt ? new Date(driver.startedAt).toLocaleString() : '-'}
              </p>
            </div>
            <div>
              <label className="text-sm text-gray-500 dark:text-gray-400">Ended At</label>
              <p className="text-lg font-medium text-gray-900 dark:text-white">
                {driver.endedAt ? new Date(driver.endedAt).toLocaleString() : '-'}
              </p>
            </div>
            <div>
              <label className="text-sm text-gray-500 dark:text-gray-400">Status</label>
              <p className="text-lg font-medium">
                <span className={`px-2 py-1 rounded-full text-xs ${
                  driver.isActive 
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                    : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                }`}>
                  {driver.isActive ? 'Active' : 'Inactive'}
                </span>
              </p>
            </div>
            {driver.other && (
              <div className="md:col-span-2">
                <label className="text-sm text-gray-500 dark:text-gray-400">Other Information</label>
                <p className="text-lg font-medium text-gray-900 dark:text-white">{driver.other}</p>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Trip History</h2>
            
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <input
                  type="date"
                  name="fromDate"
                  value={dateRange.fromDate}
                  onChange={handleDateRangeChange}
                  className="input-field text-sm w-40"
                  placeholder="From"
                />
                <span className="text-gray-500">-</span>
                <input
                  type="date"
                  name="toDate"
                  value={dateRange.toDate}
                  onChange={handleDateRangeChange}
                  className="input-field text-sm w-40"
                  placeholder="To"
                />
              </div>
              {(dateRange.fromDate || dateRange.toDate) && (
                <button
                  onClick={clearDateRange}
                  className="text-sm text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {loadingTrips ? (
            <LoadingSpinner />
          ) : trips.length > 0 ? (
            <>
              <DataTable 
                columns={tripColumns} 
                data={trips}
                pageInfo={tripsPageInfo}
                onPageChange={(page) => setTripsPageInfo({...tripsPageInfo, currentPage: page})}
              />
              
              {(dateRange.fromDate || dateRange.toDate) && (
                <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Trip Summary</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">Total Trips</span>
                      <p className="text-lg font-semibold text-gray-900 dark:text-white">{trips.length}</p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">Completed</span>
                      <p className="text-lg font-semibold text-green-600 dark:text-green-400">
                        {trips.filter(t => t.endDate || t.endedAt).length}
                      </p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">In Progress</span>
                      <p className="text-lg font-semibold text-blue-600 dark:text-blue-400">
                        {trips.filter(t => !(t.endDate || t.endedAt)).length}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <CalendarIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No trips</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                This driver has no trips yet.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DriverDetails;